import { useState, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

export const useAudioRecording = (userId, scrollId) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        } 
      });
      
      streamRef.current = stream;
      
      // Detect supported MIME type - prioritize formats iOS can play
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
        console.log('Using audio/mp4 (iOS compatible)');
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
        console.log('Using audio/webm with opus codec');
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
        console.log('Using audio/ogg with opus codec');
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
        console.log('Using audio/webm');
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
        console.log('Using audio/wav');
      } else {
        console.log('Using default audio/webm (no supported formats found)');
      }
      
      console.log('Final MIME type:', mimeType);
      
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType,
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log('Data chunk received:', event.data.size, 'bytes');
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, chunks:', chunksRef.current.length);
        const blob = new Blob(chunksRef.current, { type: mimeType });
        console.log('Final blob size:', blob.size, 'bytes, type:', blob.type);
        const url = URL.createObjectURL(blob);
        setRecordingBlob(blob);
        setRecordingUrl(url);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log('Stopped track:', track.kind);
          });
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error.name}`);
      };
      
      // Request data every second to prevent data loss on mobile
      mediaRecorder.start(1000);
      setIsRecording(true);
      setError(null);
      
      let seconds = 0;
      timerRef.current = setInterval(() => {
        seconds += 1;
        setDuration(seconds);
      }, 1000);
      
      console.log('Recording started with MIME type:', mimeType);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(`Could not access microphone: ${err.message}`);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('Stopping recording...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [isRecording]);

  const resetRecording = useCallback(() => {
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
    }
    setRecordingBlob(null);
    setRecordingUrl(null);
    setDuration(0);
    chunksRef.current = [];
  }, [recordingUrl]);

  const saveRecording = useCallback(async () => {
    if (!recordingBlob || !userId || !scrollId) {
      const errorMsg = `Missing: ${!recordingBlob ? 'recording' : ''} ${!userId ? 'userId' : ''} ${!scrollId ? 'scrollId' : ''}`;
      console.error('Cannot save:', errorMsg);
      setError('Missing recording or user information');
      return null;
    }

    try {
      console.log('Saving recording...', {
        blobSize: recordingBlob.size,
        blobType: recordingBlob.type,
        userId,
        scrollId,
        scrollIdType: typeof scrollId
      });
      
      // Determine file extension based on blob type
      let extension = 'webm';
      let contentType = recordingBlob.type;
      
      if (recordingBlob.type.includes('mp4')) {
        extension = 'mp4';
      } else if (recordingBlob.type.includes('ogg')) {
        extension = 'ogg';
      } else if (recordingBlob.type.includes('wav')) {
        extension = 'wav';
      } else if (recordingBlob.type.includes('webm')) {
        extension = 'webm';
      }
      
      console.log('File extension:', extension, 'Content-Type:', contentType);
      
      const fileName = `${userId}/${scrollId}.${extension}`;
      console.log('Uploading to:', fileName);
      
      const { data, error: uploadError } = await supabase.storage
        .from('user-recordings')
        .upload(fileName, recordingBlob, {
          upsert: true,
          contentType: contentType
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }
      
      console.log('Upload successful:', data);

      const { data: { publicUrl } } = supabase.storage
        .from('user-recordings')
        .getPublicUrl(fileName);
      
      // Add cache-busting parameter to force browser to reload new file
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
      
      console.log('Public URL:', publicUrl);
      console.log('Cache-busted URL:', cacheBustedUrl);
      console.log('About to save to database...');

      // First, check if a recording already exists
      const { data: existingRecording } = await supabase
        .from('user_recordings')
        .select('id')
        .eq('user_id', userId)
        .eq('scroll_id', scrollId)
        .single();

      let dbData, dbError;

      if (existingRecording) {
        // Update existing record
        console.log('Updating existing recording...');
        const result = await supabase
          .from('user_recordings')
          .update({
            recording_url: cacheBustedUrl,
            duration_seconds: Math.floor(duration),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('scroll_id', scrollId);
        
        dbData = result.data;
        dbError = result.error;
      } else {
        // Insert new record
        console.log('Inserting new recording...');
        const result = await supabase
          .from('user_recordings')
          .insert({
            user_id: userId,
            scroll_id: scrollId,
            recording_url: cacheBustedUrl,
            duration_seconds: Math.floor(duration),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        dbData = result.data;
        dbError = result.error;
      }

      if (dbError) {
        console.error('Database error details:', {
          message: dbError.message,
          details: dbError.details,
          hint: dbError.hint,
          code: dbError.code,
          scrollId: scrollId,
          scrollIdType: typeof scrollId
        });
        throw dbError;
      }
      
      console.log('Database operation successful:', dbData);
      console.log('Recording saved successfully!');
      return cacheBustedUrl;
      
    } catch (err) {
      console.error('Error saving recording:', err);
      setError(`Failed to save: ${err.message}`);
      return null;
    }
  }, [recordingBlob, userId, scrollId, duration]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    isRecording,
    recordingBlob,
    recordingUrl,
    duration: formatDuration(duration),
    error,
    startRecording,
    stopRecording,
    resetRecording,
    saveRecording
  };
};